import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import QuickQuoteCalculator from '@/components/QuickQuoteCalculator';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Settings, FileText, Download, CheckCircle, Factory, Zap, Shield } from 'lucide-react';
import { downloadCatalogue } from '@/utils/catalogueDownload';
import { toast } from 'sonner';

const HomePage = () => {
  const beltTypes = [
    {
      id: 'conveyor-belts',
      name: 'Conveyor Belts',
      description: 'Heavy-duty material handling solutions with cleated, rough top & food-grade options',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/8/331023420/ZW/MD/SU/1393287/flat-belting-industrial-belting-500x500.jpg'
    },
    {
      id: 'heavy-duty-v-belts',
      name: 'Heavy Duty V-Belts',
      description: 'Banded, perforated & classical V-belts for power transmission',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/12/366802659/VT/BX/IJ/1393287/banded-v-belts-500x500.jpg'
    },
    {
      id: 'caterpillar-belts',
      name: 'Caterpillar Belts',
      description: 'Industrial haul-off & rubber caterpillar belts for specialized applications',
      image: 'https://5.imimg.com/data5/IOS/Default/2022/9/RQ/RR/US/1393287/product-jpeg-500x500.png'
    },
    {
      id: 'take-up-drive-belts',
      name: 'Take Up Drive Belts',
      description: 'Top T Bottom V design for precision drive applications',
      image: 'https://5.imimg.com/data5/IOS/Default/2022/9/BA/ZG/ID/1393287/product-jpeg-500x500.png'
    },
    {
      id: 'wedge-belts',
      name: 'Wedge Belts',
      description: 'Variable speed & hexagonal V-belts for efficient power transmission',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/12/366796399/PA/NM/CX/1393287/variable-speed-belt-250x250.jpg'
    },
    {
      id: 'food-grade-belts',
      name: 'Food Grade Belts',
      description: 'FDA & EU compliant hygienic belts for food processing industries',
      image: 'https://5.imimg.com/data5/SELLER/Default/2025/7/526497469/QY/CW/OH/1393287/21oqb1hfqal-uf1000-1000-ql80-500x500.jpg'
    }
  ];

  const features = [
    { icon: Factory, title: 'Since 1951', description: '70+ years of industrial expertise' },
    { icon: CheckCircle, title: 'ISO Certified', description: 'Quality you can trust' },
    { icon: Zap, title: 'Fast Delivery', description: 'Quick turnaround times' },
    { icon: Shield, title: 'Warranty', description: 'Backed by comprehensive warranty' }
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] pt-44 pb-20 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#60A5FA]/20 border border-[#60A5FA]/40 rounded-full mb-6" data-testid="since-badge">
                <span className="text-[#60A5FA] text-sm font-medium tracking-wider">ESTABLISHED 1951</span>
              </div>
              <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight" data-testid="hero-title">
                POWER TRANSMISSION
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#60A5FA] to-[#93C5FD]">BELTS & SOLUTIONS</span>
              </h1>
              <p className="text-lg text-gray-300 mb-8 max-w-xl">
                Leading manufacturer and supplier of industrial belting solutions for OEM manufacturers, steel plants, cement plants, and power plants worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-blue-500/25 transition-all duration-300"
                  data-testid="configure-belt-btn"
                >
                  <Link to="/configure">
                    <FileText className="mr-2" size={20} />
                    Request for Quotation
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#60A5FA]/50 text-white hover:bg-[#60A5FA]/10 px-8 py-6 text-base rounded-lg transition-all duration-300"
                  data-testid="selection-guide-btn"
                >
                  <Link to="/selection-guide">
                    <Settings className="mr-2" size={20} />
                    Selection Guide
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#2563EB]/20 to-[#60A5FA]/20 rounded-2xl blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZyUyMGZhY2lsaXR5JTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzE5MTUxODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Industrial Manufacturing Facility"
                className="relative w-full h-[400px] object-cover rounded-xl border border-[#60A5FA]/30 shadow-2xl"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="glass-card p-6 hover:shadow-lg hover:border-[#2563EB]/30 transition-all duration-300" data-testid={`feature-${index}`}>
                  <Icon className="text-[#2563EB] mb-4" size={32} />
                  <h3 className="font-bold text-[#1E293B] text-lg mb-2">{feature.title}</h3>
                  <p className="text-[#64748B] text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Quote Calculator Section */}
      <section className="py-20 px-6 lg:px-12 bg-gray-50" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="heading-font text-3xl sm:text-4xl text-[#1E293B] mb-4">
              GET INSTANT PRICE ESTIMATE
            </h2>
            <p className="text-[#64748B] text-base max-w-2xl mx-auto">
              No waiting, no hassle. Configure your belt and get an estimated price in seconds
            </p>
          </div>
          <QuickQuoteCalculator />
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-bold text-3xl sm:text-4xl text-[#1E293B] mb-4" data-testid="products-title">
              OUR PRODUCT RANGE
            </h2>
            <p className="text-[#64748B] text-base max-w-2xl mx-auto">
              Comprehensive selection of power transmission and conveyor belts for diverse industrial applications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {beltTypes.map((belt, index) => (
              <Link 
                key={index} 
                to={`/products?category=${belt.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl border border-gray-100 hover:border-[#2563EB]/30 transition-all duration-300 group cursor-pointer" 
                data-testid={`belt-type-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={belt.image}
                    alt={belt.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="font-bold text-white text-lg">{belt.name}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-[#64748B] text-sm mb-4">{belt.description}</p>
                  <span className="text-[#2563EB] font-semibold flex items-center">
                    View Details <ArrowRight size={16} className="ml-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="font-bold text-3xl sm:text-4xl text-white mb-6" data-testid="cta-title">
            READY TO GET STARTED?
          </h2>
          <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
            Request a quotation for your custom belt solution or download our comprehensive product catalogue
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white px-8 py-6 rounded-lg shadow-lg shadow-blue-500/25 transition-all duration-300"
              data-testid="cta-rfq-btn"
            >
              <Link to="/configure">
                Request for Quotation
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-[#60A5FA]/50 text-white hover:bg-[#60A5FA]/10 px-8 py-6 rounded-lg transition-all duration-300"
              data-testid="download-catalogue-btn"
              onClick={async () => {
                toast.info('Generating PDF catalogue...');
                try {
                  await downloadCatalogue();
                  toast.success('Product catalogue downloaded successfully!');
                } catch (error) {
                  toast.error('Failed to download catalogue. Please try again.');
                }
              }}
            >
              <Download className="mr-2" size={20} />
              Download Catalogue
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;